import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FlightExchangePolicySelectors,
  FlightExchangeSearchSelectors,
} from "@b2bportal/core-flights";
import {
  type IPriceLineItem,
  type ISummaryLineItem,
  TripCategory,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";

import type { IFlightExchangePriceBreakdownProps } from "./FlightExchangePriceBreakdown";

export const useFlightExchangePriceBreakdown = (
  props: IFlightExchangePriceBreakdownProps
) => {
  const { showFareDescription = true } = props;
  const { formatFiatCurrency, t } = useI18nContext();
  const newBookingLineItems = useSelector(
    FlightExchangePolicySelectors.getNewBookingLineItems
  );
  const summaryLineItems = useSelector(
    FlightExchangePolicySelectors.getSummaryLineItems
  );
  const tripCategory = useSelector(
    FlightExchangeSearchSelectors.getTripCategory
  );

  const parsedPriceLineItems = useMemo(() => {
    return newBookingLineItems.map<IPriceLineItem>((lineItem) => ({
      baseAmount: {
        label: t(lineItem.label),
        value: formatFiatCurrency({
          currencyCode: lineItem.currency,
          value: lineItem.amount,
        }),
      },
    }));
  }, [formatFiatCurrency, newBookingLineItems, t]);

  const parsedSummaryLineItems = useMemo(() => {
    return summaryLineItems.map<ISummaryLineItem>((lineItem) => ({
      label: t(lineItem.label),
      value: formatFiatCurrency({
        currencyCode: lineItem.currency,
        value: lineItem.amount,
      }),
      type: lineItem.type,
    }));
  }, [formatFiatCurrency, summaryLineItems, t]);

  return useMemo(() => {
    let subtitle: string | undefined = undefined;

    if (showFareDescription) {
      subtitle = t("flightPriceBreakdown.oneWayDescription");

      if (tripCategory === TripCategory.ROUND_TRIP) {
        subtitle = t("flightPriceBreakdown.roundTripDescription");
      }
    }

    return {
      context: {
        currency: summaryLineItems[0].currency,
        priceLineItems: parsedPriceLineItems,
        summaryLineItems: parsedSummaryLineItems,
      },
      handlers: {},
      text: {
        subtitle,
        title: t("priceBreakdown"),
      },
    };
  }, [
    parsedPriceLineItems,
    parsedSummaryLineItems,
    showFareDescription,
    summaryLineItems,
    t,
    tripCategory,
  ]);
};

export type IFlightExchangePriceBreakdownData = ReturnType<
  typeof useFlightExchangePriceBreakdown
>;
